/**
 * Client-side photo pipeline (SPEC §6).
 *
 * Photos are resized and compressed **before** they leave the device: cover-crop
 * to 400×500 (the license-card aspect), encoded WebP, quality stepped down until
 * the payload lands in the ~30–50 KB band. The protocol schema rejects anything
 * over 400 KB and `PHOTO_MAX_BYTES` (80 KB) is our own hard ceiling — the DO
 * stores these, they are pushed to every member over the socket, and they never
 * get a URL.
 *
 * Everything here is source-agnostic: a live selfie `Blob` from the camera and a
 * camera-roll `File` go through exactly the same path, which is why upload can
 * be offered as a genuinely equal alternative rather than a degraded one.
 */
import { PHOTO_MAX_BYTES, PHOTO_MAX_HEIGHT, PHOTO_MAX_WIDTH, PHOTO_MIME } from '@idgame/protocol';

/** Quality ladder. First rung that fits the target wins. */
const QUALITY_STEPS = [0.82, 0.72, 0.62, 0.52, 0.42, 0.34, 0.26] as const;

/**
 * SPEC §6 asks for ~30–50 KB. This is the number we aim at and almost always
 * hit; `PHOTO_MAX_BYTES` (80 KB, frozen in the protocol) is a ceiling that is
 * **never** exceeded — `processPhoto` throws rather than return something
 * larger, because a payload over the ceiling would be silently rejected by the
 * server after the user believed they were done.
 */
const TARGET_BYTES = 50_000;

/** How many times the canvas may be shrunk before we accept the best we have. */
const MAX_SHRINK_PASSES = 8;

/** A card narrower than this stops being a recognisable face. */
const MIN_WIDTH = 160;

export interface ProcessedPhoto {
  /** `data:image/webp;base64,…` — what goes on the wire. */
  dataUrl: string;
  /** Decoded byte length of the encoded image (not the base64 string). */
  bytes: number;
  width: number;
  height: number;
  mime: string;
  /**
   * True when the browser could not encode WebP and we fell back to JPEG.
   * SPEC §6 specifies WebP, but failing someone's join on an older browser is
   * worse than a slightly larger file — so the fallback stays, and says so
   * rather than substituting silently.
   */
  usedFallbackFormat: boolean;
}

/** Thrown when an image cannot be squeezed under the hard ceiling. */
export class PhotoTooLargeError extends Error {
  readonly maxBytes: number;

  constructor(maxBytes: number) {
    super(`could not compress that image below ${Math.round(maxBytes / 1000)} KB`);
    this.name = 'PhotoTooLargeError';
    this.maxBytes = maxBytes;
  }
}

/** A rectangle in source pixels. */
export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ProcessPhotoOptions {
  maxWidth?: number;
  maxHeight?: number;
  /** Hard ceiling. Never exceeded; `processPhoto` throws instead. */
  maxBytes?: number;
  /** What we aim for. Defaults to ~50 KB (SPEC §6). */
  targetBytes?: number;
  /** Mirror the image horizontally — selfies look wrong un-mirrored. */
  mirror?: boolean;
  /**
   * The region of the source the card is cut from. Omitted, the centre wins,
   * which is what an un-repositioned crop window is sitting over anyway.
   */
  crop?: CropRect;
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Resize + compress an image source to a license-card-shaped WebP data URL.
 *
 * Accepts anything `loadImage` can decode: a `File` from `<input type="file">`,
 * a `Blob` captured from the camera, or an already-decoded bitmap-ish element.
 */
export async function processPhoto(
  source: Blob | HTMLImageElement | HTMLVideoElement | HTMLCanvasElement,
  options: ProcessPhotoOptions = {},
): Promise<ProcessedPhoto> {
  const maxWidth = options.maxWidth ?? PHOTO_MAX_WIDTH;
  const maxHeight = options.maxHeight ?? PHOTO_MAX_HEIGHT;
  const maxBytes = options.maxBytes ?? PHOTO_MAX_BYTES;
  const targetBytes = Math.min(options.targetBytes ?? TARGET_BYTES, maxBytes);
  const mirror = options.mirror ?? false;
  const crop = options.crop;

  const drawable = source instanceof Blob ? await loadImage(source) : source;

  try {
    const aspect = maxHeight / maxWidth;
    let width = maxWidth;
    let height = maxHeight;
    /** Best encoding seen that is at least under the hard ceiling. */
    let underCeiling: ProcessedPhoto | null = null;

    // Outer loop shrinks the canvas; inner loop walks the quality ladder. Most
    // photos hit the target on the first or second rung; a very noisy one needs
    // pixels taking away, not just quality.
    for (let pass = 0; pass < MAX_SHRINK_PASSES; pass += 1) {
      const canvas = drawCover(drawable, width, height, mirror, crop);

      for (const quality of QUALITY_STEPS) {
        const dataUrl = encode(canvas, quality);
        const bytes = dataUrlByteLength(dataUrl);
        const candidate: ProcessedPhoto = {
          dataUrl,
          bytes,
          width,
          height,
          mime: mimeOf(dataUrl),
          usedFallbackFormat: !dataUrl.startsWith(`data:${PHOTO_MIME}`),
        };

        if (bytes <= targetBytes) return candidate;
        if (bytes <= maxBytes && (!underCeiling || bytes < underCeiling.bytes)) {
          underCeiling = candidate;
        }
      }

      if (width <= MIN_WIDTH) break;
      width = Math.max(MIN_WIDTH, Math.round(width * 0.75));
      height = Math.round(width * aspect);
    }

    // Never hit the target, but something fits the ceiling — take it.
    if (underCeiling) return underCeiling;

    // Nothing fits even at the floor quality and the minimum size. Returning an
    // oversized payload would defeat the whole point: the server would reject
    // it after the user believed they were finished. Fail loudly instead so the
    // capture screen can ask for a different photo.
    throw new PhotoTooLargeError(maxBytes);
  } finally {
    if (source instanceof Blob && drawable instanceof HTMLImageElement && drawable.src) {
      URL.revokeObjectURL(drawable.src);
    }
  }
}

/** Decode a Blob/File into an `<img>` we can draw. */
export function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('could not read that image'));
    };
    img.src = url;
  });
}

/** Byte length of the payload a data URL carries, ignoring the header. */
export function dataUrlByteLength(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  if (comma === -1) return 0;
  const body = dataUrl.slice(comma + 1);
  if (!dataUrl.slice(0, comma).includes(';base64')) return body.length;
  const padding = body.endsWith('==') ? 2 : body.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((body.length * 3) / 4) - padding);
}

/** `image/webp` for a WebP data URL, etc. */
export function mimeOf(dataUrl: string): string {
  const match = /^data:([^;,]+)/.exec(dataUrl);
  return match?.[1] ?? 'application/octet-stream';
}

/** True when this browser can actually encode WebP from a canvas. */
export function supportsWebp(): boolean {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL(PHOTO_MIME).startsWith(`data:${PHOTO_MIME}`);
  } catch {
    return false;
  }
}

/* -------------------------------------------------------------------------- */
/* Internals                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Cover-crop: fill the target box entirely, centre-cropping the overflow. A
 * license card with letterbox bars in it looks broken, so we never contain.
 *
 * With a `crop`, the same rule is applied to that region instead of the whole
 * image — the player has already chosen which part of the frame is the card.
 */
function drawCover(
  source: CanvasImageSource & { width?: number; height?: number },
  targetWidth: number,
  targetHeight: number,
  mirror: boolean,
  crop?: CropRect,
): HTMLCanvasElement {
  const naturalWidth = intrinsicWidth(source);
  const naturalHeight = intrinsicHeight(source);

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');

  if (naturalWidth === 0 || naturalHeight === 0) return canvas;

  const region = clampRect(crop, naturalWidth, naturalHeight);

  const scale = Math.max(targetWidth / region.width, targetHeight / region.height);
  const drawWidth = region.width * scale;
  const drawHeight = region.height * scale;
  const dx = (targetWidth - drawWidth) / 2;
  const dy = (targetHeight - drawHeight) / 2;

  ctx.imageSmoothingQuality = 'high';
  if (mirror) {
    ctx.translate(targetWidth, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(
    source,
    region.x,
    region.y,
    region.width,
    region.height,
    dx,
    dy,
    drawWidth,
    drawHeight,
  );

  return canvas;
}

/** A crop that has wandered off the image is clamped back onto it. */
function clampRect(crop: CropRect | undefined, width: number, height: number): CropRect {
  if (!crop || crop.width <= 0 || crop.height <= 0) return { x: 0, y: 0, width, height };
  const w = Math.min(crop.width, width);
  const h = Math.min(crop.height, height);
  return {
    x: Math.min(Math.max(crop.x, 0), width - w),
    y: Math.min(Math.max(crop.y, 0), height - h),
    width: w,
    height: h,
  };
}

/* -------------------------------------------------------------------------- */
/* Where the crop window is pointing                                           */
/* -------------------------------------------------------------------------- */

/**
 * Translate the crop window the player dragged — laid out in CSS pixels over a
 * frame the photo is `object-cover`ed into — back into source pixels.
 *
 * This is the whole reason "Drag the crop to reposition." is honest: the same
 * cover transform the browser used to paint the frame is reversed here, so what
 * is inside the white box is exactly what gets encoded.
 */
export function windowToSourceRect(
  frame: { width: number; height: number },
  natural: { width: number; height: number },
  window: CropRect,
): CropRect | null {
  if (frame.width <= 0 || frame.height <= 0) return null;
  if (natural.width <= 0 || natural.height <= 0) return null;
  if (window.width <= 0 || window.height <= 0) return null;

  const scale = Math.max(frame.width / natural.width, frame.height / natural.height);
  const paintedX = (frame.width - natural.width * scale) / 2;
  const paintedY = (frame.height - natural.height * scale) / 2;

  return clampRect(
    {
      x: (window.x - paintedX) / scale,
      y: (window.y - paintedY) / scale,
      width: window.width / scale,
      height: window.height / scale,
    },
    natural.width,
    natural.height,
  );
}

function encode(canvas: HTMLCanvasElement, quality: number): string {
  const webp = canvas.toDataURL(PHOTO_MIME, quality);
  // Safari <14 and jsdom silently fall back to PNG, which is far too big.
  // JPEG is the correct second choice; the schema only requires `data:image/`.
  if (webp.startsWith(`data:${PHOTO_MIME}`)) return webp;
  return canvas.toDataURL('image/jpeg', quality);
}

function intrinsicWidth(source: CanvasImageSource): number {
  if (typeof HTMLVideoElement !== 'undefined' && source instanceof HTMLVideoElement) {
    return source.videoWidth;
  }
  const width = (source as { width?: number | { baseVal: { value: number } } }).width;
  return typeof width === 'number' ? width : 0;
}

function intrinsicHeight(source: CanvasImageSource): number {
  if (typeof HTMLVideoElement !== 'undefined' && source instanceof HTMLVideoElement) {
    return source.videoHeight;
  }
  const height = (source as { height?: number | { baseVal: { value: number } } }).height;
  return typeof height === 'number' ? height : 0;
}
